import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "./hooks";
import { useOrganizationLanguages } from "./useOrganizationLanguages";
import { publishOrganizationLanguages } from "../redux/organization/publishOrganizationLanguages";
import type { RootState } from "../redux/store";

/**
 * Fills in the selected organisation's language settings when the store holds
 * only a list copy of it. Admin, Owner, SuperAdmin and P3M staff get their
 * selected organisation from list projections that carry neither
 * `assessmentLanguages` nor `customization.translation`, so without this the
 * content language never sees the organisation's default and those people
 * read English when the school starts in another language.
 *
 * While the selected organisation carries no language list, its settings are
 * read once (`GET /organization/:id/languages`) and published into every
 * stored copy of it; the published copy carries the list, so nothing is read
 * again for it, and selecting another organisation that lacks one reads that
 * one. A failed read stays quiet: the copy stays as it was and the content
 * language stays what it was (English, unless the person chose a language of
 * their own).
 *
 * Mounted once for the signed-in app (`OrganizationLanguagesReader`).
 */
export function useSelectedOrganizationLanguages(): void {
    const dispatch = useAppDispatch();
    const token = useAppSelector(
        (state: RootState) => state?.auth?.access_token
    );
    const organization = useAppSelector(
        (state: RootState) => state?.organization?.selectedOrganization
    );

    const lacking =
        organization?._id && !Array.isArray(organization.assessmentLanguages)
            ? organization._id
            : undefined;
    const settings = useOrganizationLanguages(lacking, {
        Authorization: `Bearer ${token}`,
    });

    useEffect(() => {
        if (lacking && settings) {
            publishOrganizationLanguages(dispatch, lacking, settings);
        }
    }, [dispatch, lacking, settings]);
}
